import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { channelLabel, type AutomationListItem, type Channel } from '@neuroedge/shared';
import { Button, Card, PageState, useToast } from '../../components/ui';
import {
  DataGrid,
  applyClientGrid,
  useGridState,
  type ClientColumnSpec,
  type GridColumn,
} from '../../components/data-grid/DataGrid';
import { useAuth } from '../../auth/AuthContext';
import { useChannels } from '../../api/channels';
import {
  useAutomations,
  useDeleteAutomation,
  useActivateAutomation,
  useDeactivateAutomation,
} from '../../api/automations';
import { useAutomationFlows } from '../../api/automation-flows';
import { describeAutomation, knownReplyOptions } from './automation-model';

const ALL_CHANNELS_KEY = '__all__';

const STATUS_LABEL: Record<AutomationListItem['status'], string> = {
  ACTIVE: 'On',
  INACTIVE: 'Off',
  DRAFT: 'Draft (off)',
  ARCHIVED: 'Archived',
};

type Tab = 'ALL' | AutomationListItem['status'];
const TABS: { id: Tab; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'ACTIVE', label: 'On' },
  { id: 'INACTIVE', label: 'Off' },
  { id: 'DRAFT', label: 'Draft' },
  { id: 'ARCHIVED', label: 'Archived' },
];

function RowActions({
  automation,
  canUpdate,
}: {
  automation: AutomationListItem;
  canUpdate: boolean;
}) {
  const navigate = useNavigate();
  const del = useDeleteAutomation();
  const activate = useActivateAutomation();
  const deactivate = useDeactivateAutomation();
  const toast = useToast();

  async function remove() {
    if (!window.confirm(`Delete "${automation.name}"? This cannot be undone.`)) return;
    try {
      await del.mutateAsync(automation.id);
      toast.success('Automation deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the automation.');
    }
  }

  async function toggleActive() {
    try {
      if (automation.status === 'ACTIVE') {
        await deactivate.mutateAsync(automation.id);
        toast.success('Automation deactivated.');
      } else {
        await activate.mutateAsync(automation.id);
        toast.success('Automation activated.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change the automation status.');
    }
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
      <Button
        variant="secondary"
        onClick={() => navigate(`/automations/${automation.id}/executions`)}
      >
        Executions
      </Button>
      {canUpdate ? (
        <>
          {automation.status !== 'ARCHIVED' ? (
            <Button
              variant="secondary"
              disabled={activate.isPending || deactivate.isPending}
              onClick={() => void toggleActive()}
            >
              {automation.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          ) : null}
          <Button
            variant="secondary"
            onClick={() => navigate(`/automations/${automation.id}/edit`)}
          >
            Edit
          </Button>
          <Button variant="secondary" disabled={del.isPending} onClick={() => void remove()}>
            Delete
          </Button>
        </>
      ) : null}
    </div>
  );
}

/**
 * Automations on the standard grid: On / Off / Draft / Archived tabs with
 * counts, filters for name/summary and channel, sorting and paging (in the
 * browser - a tenant has tens of automations). Each row keeps its
 * plain-English "When ... then ..." summary and its actions.
 */
export function AutomationsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('automation.create');
  const canUpdate = hasPermission('automation.update');

  const { data, isError, error, refetch, isFetching } = useAutomations({ pageSize: 1000 });
  const channels = useChannels();
  const flows = useAutomationFlows();
  // Plain-English "When ... then ..." line per automation (automation-model.ts).
  const summaries = useMemo(() => {
    const items = data?.items ?? [];
    const buttonTitles = Object.fromEntries(knownReplyOptions(items).map((o) => [o.id, o.title]));
    const flowName = (key: string) => flows.data?.items.find((f) => f.key === key)?.name;
    return Object.fromEntries(
      items.map((a) => [a.id, describeAutomation(a, { flowName, buttonTitles })]),
    );
  }, [data?.items, flows.data]);

  const channelName = (id: string | null) => {
    if (!id) return 'All WhatsApp numbers';
    const c = (channels.data ?? []).find((ch: Channel) => ch.id === id);
    return c ? channelLabel(c) : 'Unknown channel';
  };

  const [tab, setTab] = useState<Tab>('ALL');
  const [grid, setGrid] = useGridState({ sortBy: 'name', sortOrder: 'asc' });
  const all = data?.items ?? [];
  const inTab = tab === 'ALL' ? all : all.filter((a) => a.status === tab);
  const spec: Record<string, ClientColumnSpec<AutomationListItem>> = {
    name: {
      text: (a) => `${a.name} ${summaries[a.id] ?? ''} ${a.description ?? ''}`,
      sort: (a) => a.name,
    },
    channelId: {
      equals: (a) => a.channelId ?? ALL_CHANNELS_KEY,
      sort: (a) => channelName(a.channelId),
    },
    status: { sort: (a) => STATUS_LABEL[a.status] },
    updatedAt: { date: (a) => a.updatedAt },
  };
  const page = applyClientGrid(inTab, grid, spec);

  const columns: GridColumn<AutomationListItem>[] = [
    {
      key: 'name',
      header: 'Automation',
      sortable: true,
      filter: { type: 'text', placeholder: 'Name or what it does' },
      render: (a) => (
        <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
          <strong>{a.name}</strong>
          <span>{summaries[a.id] ?? ''}</span>
          {a.description ? (
            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
              {a.description}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'channelId',
      header: 'Channel',
      sortable: true,
      filter: {
        type: 'select',
        options: [
          ...(channels.data ?? []).map((c: Channel) => ({ value: c.id, label: channelLabel(c) })),
          { value: ALL_CHANNELS_KEY, label: 'All WhatsApp numbers' },
        ],
      },
      render: (a) => channelName(a.channelId),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (a) => <span className="ne-badge">{STATUS_LABEL[a.status]}</span>,
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      filter: { type: 'dateRange' },
      render: (a) => new Date(a.updatedAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      render: (a) => <RowActions automation={a} canUpdate={canUpdate} />,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Automations</h1>
        <span style={{ flex: 1 }} />
        {canCreate ? (
          <Button onClick={() => navigate('/automations/new')}>New automation</Button>
        ) : null}
      </div>

      <Card>
        <div role="tablist" aria-label="Automation status" className="ne-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className="ne-tabs__tab"
              onClick={() => {
                setTab(t.id);
                setGrid({ ...grid, page: 1 });
              }}
            >
              {t.label}
              {data ? (
                <span className="ne-tabs__count">
                  {t.id === 'ALL' ? all.length : all.filter((a) => a.status === t.id).length}
                </span>
              ) : null}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 'var(--space-3)' }}>
          {isError ? (
            <PageState kind="error" message={error.message} onRetry={() => void refetch()} />
          ) : (
            <DataGrid
              columns={columns}
              rows={page.rows}
              rowKey={(a) => a.id}
              rowLabel={(a) => a.name}
              total={page.total}
              state={grid}
              onStateChange={setGrid}
              loading={isFetching}
              emptyMessage={
                all.length === 0
                  ? 'No automations yet - create your first automation.'
                  : 'No automations match these filters.'
              }
            />
          )}
        </div>
      </Card>

      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/">← Back to dashboard</Link>
      </p>
    </div>
  );
}
